import Player from "@vimeo/player";
import { useEffect, useRef, useState } from "react";
import "../../../assets/css/player.css";

function VimeoPlayer(props) {
  const videoId = props.videoId;
  const onPlay = props.onPlay;
  const onCompleted = props?.onCompleted ?? null;
  const onStart = props?.onStart ?? null;
  const onError = props?.onError ?? null;
  const pauseVideo = props?.showPromoVideo ?? null;
  const allowCompletionOnError = props?.allowCompletionOnError ?? true; // Default to true for backward compatibility
  const videoWatchedPercentage = 87;
  const previousVideo = useRef({ videoId }).current;
  const [player, setPlayer] = useState(null);
  const [watchedCompletely, setWatchedCompletely] = useState(false);
  const [startWatching, setStartWatching] = useState(false);

  useEffect(() => {
    previousVideo.videoId !== videoId ? loadVideo(videoId) : VimeoPlayer(videoId);

    return () => {
      previousVideo.videoId = videoId;
    };
  }, [videoId]);

  useEffect(() => {
    if (onCompleted && watchedCompletely) {
      onCompleted(watchedCompletely);
    }
  }, [watchedCompletely]);

  useEffect(() => {
    if (onStart && startWatching) {
      onStart();
    }
  }, [startWatching]);

  useEffect(() => {
    if (!pauseVideo && player) {
      player.pause();
    }
  }, [pauseVideo, player]);

  const VimeoPlayer = (videoId) => {
    let videoWatched = false;
    let errorTimeout;

    // Validate video ID before attempting to create player
    if (!videoId || !/^\d+$/.test(videoId) || videoId.length < 3) {
      console.warn('Invalid Vimeo video ID, marking as completed');
      if (allowCompletionOnError) {
        setWatchedCompletely(true);
        setStartWatching(true);
      } else {
        // For welcome video, call onError to mark step as completed when video is invalid
        if (onError) {
          onError();
        }
      }
      return;
    }

    const options = {
      id: videoId,
      autoplay: false,
      responsive: true,
      background: false,
      controls: true,
    };

    // Set a timeout to handle cases where video fails to load
    errorTimeout = setTimeout(() => {
      console.warn("Video loading timeout, marking as completed");
      if (allowCompletionOnError) {
        setWatchedCompletely(true);
        setStartWatching(true);
      } else {
        // For welcome video, call onError to mark step as completed when video times out
        if (onError) {
          onError();
        }
      }
    }, 10000); // 10 second timeout

    try {
      let player = new Player("vimeo_player", options);
      setPlayer(player);

      player.on("play", function () {
        clearTimeout(errorTimeout);
        if (onPlay) {
          onPlay();
        }
        videoWatched = false;
        setStartWatching(true); // Update startWatching when the video starts playing
      });

      player.on("timeupdate", (data) => {
        if (!videoWatched && data.percent >= videoWatchedPercentage / 100) {
          setWatchedCompletely(true);
          videoWatched = true;
        }
      });

      // Handle video loading errors
      player.on("error", function (error) {
        clearTimeout(errorTimeout);
        console.warn("Video loading error:", error);
        // Mark video as completed so user can proceed to next step (only if allowed)
        if (allowCompletionOnError) {
          setWatchedCompletely(true);
          setStartWatching(true);
        } else {
          // For welcome video, call onError to mark step as completed when video has errors
          if (onError) {
            onError();
          }
        }
      });

      // Handle video not found errors
      player.on("loadstart", function () {
        clearTimeout(errorTimeout);
        // Video started loading successfully
      });

      // Handle successful video load
      player.on("loaded", function () {
        clearTimeout(errorTimeout);
        console.log("Video loaded successfully");
      });

    } catch (error) {
      clearTimeout(errorTimeout);
      console.warn("Failed to create Vimeo player:", error);
      // Mark video as completed so user can proceed to next step (only if allowed)
      if (allowCompletionOnError) {
        setWatchedCompletely(true);
        setStartWatching(true);
      } else {
        // For welcome video, call onError to mark step as completed when player creation fails
        if (onError) {
          onError();
        }
      }
    }
  };

  const loadVideo = (videoId) => {
    // Validate video ID before attempting to load
    if (!videoId || !/^\d+$/.test(videoId) || videoId.length < 3) {
      console.warn('Invalid Vimeo video ID in loadVideo, marking as completed');
      if (allowCompletionOnError) {
        setWatchedCompletely(true);
        setStartWatching(true);
      } else {
        // For welcome video, call onError to mark step as completed when video ID is invalid
        if (onError) {
          onError();
        }
      }
      return;
    }

    try {
      if (player) {
        player.loadVideo(videoId);
        setWatchedCompletely(false);
        setStartWatching(false); // Reset startWatching when loading a new video
      }
    } catch (error) {
      console.warn("Failed to load video:", error);
      // Mark video as completed so user can proceed to next step (only if allowed)
      if (allowCompletionOnError) {
        setWatchedCompletely(true);
        setStartWatching(true);
      } else {
        // For welcome video, call onError to mark step as completed when video loading fails
        if (onError) {
          onError();
        }
      }
    }
  };

  return (
    <>
      <div id="vimeo_player"></div>
    </>
  );
}

export default VimeoPlayer;
